import useTranslator from "@pma/hooks/use-translator";
import { ChevronsUpDown, Users } from "lucide-react";
import { Button } from "@components/common/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@components/common/dialog";
import axios from "axios";
import { FINANCIAL_SERVER_URL, SET_USER_PROJECT_ROLE_ENDPOINT } from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "@pma/hooks/use-toast";
import { DictType } from "@pma/types";
import { useAuth } from "@pma/stores/auth-store";
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar";
import { cn } from "@pma/utils/helpers";
import { useState } from "react";
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@components/common/popover";
import { getFilteredProjectsByCurrentUser } from "@pma/utils/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command";
import { Label } from "@components/common/label";

interface Props {
  revalidate: CallableFunction;
  role: DictType;
}

const AssignProjectRoleWithoutProjectId = ({ revalidate, role }: Props) => {
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");

  const { translate } = useTranslator();

  const { authConfig } = useAuth();

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const organizationUsers = authConfig?.organizationUsers || [];
  const orgId = authConfig?.last_organization_id;

  const handleDialogClose = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSelectedUserId("");
      setSelectedProjectId("");
    }
  };

  const { data: projects } = useQuery({
    queryKey: ["projects"],
    queryFn: () =>
      getFilteredProjectsByCurrentUser({
        by_organization_id: orgId,
        status_id: 0,
        paging_per_page: 30,
      }),
  });

  const assignRole = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + SET_USER_PROJECT_ROLE_ENDPOINT,
      {
        user_id: selectedUserId,
        projects_id: selectedProjectId,
        role_type_id: role.id,
      },

      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const assignRoleMutation = useMutation({
    mutationKey: ["assign-role"],
    mutationFn: assignRole,
    onSuccess: () => {
      revalidate();
      toast({
        title: translate("Success"),
        description: translate("Role assigned"),
        variant: "success",
      });
      handleDialogClose(false);
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to assign role"),
        variant: "error",
      });
    },
  });

  const selectedUser = organizationUsers?.find((user: DictType) => user.id == selectedUserId);
  const selectedProject = projects?.items?.find(
    (project: DictType) => project?.id == selectedProjectId,
  );

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogTrigger asChild className=''>
        <Button variant='ghost' className='text-xs'>
          {translate("Assign")}
        </Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>{translate("Assign financial role to user in project")}</DialogTitle>
          <DialogDescription>
            {translate("Select a user and a project to create a role assignment")}
          </DialogDescription>
        </DialogHeader>

        <div className='grid grid-cols-1 gap-6 py-1 sm:grid-cols-2'>
          <div className='space-y-2'>
            <Label htmlFor='user-select' className='text-sm font-medium'>
              {translate("User")}
            </Label>
            <Popover>
              <PopoverTrigger
                disabled={assignRoleMutation.isPending}
                className='border rounded-md p-1.5 flex justify-between items-center w-full'
              >
                <div className='text-sm'>
                  {selectedUser ? (
                    <div className='w-[23ch] text-left font-semibold capitalize text-xs p-0.5 truncate text-ellipsis overflow-hidden'>
                      {selectedUser?.name}
                    </div>
                  ) : (
                    translate("Select User")
                  )}
                </div>
                <ChevronsUpDown size={12} />
              </PopoverTrigger>

              <PopoverContent className='p-0'>
                <Command>
                  <CommandInput placeholder={translate("Search for user") + "..."} />
                  <CommandList>
                    <CommandEmpty>{translate("No users found")}.</CommandEmpty>
                    <CommandGroup>
                      <PopoverClose className='w-full'>
                        {organizationUsers?.map((user: DictType, index: number) => (
                          <>
                            <CommandItem
                              value={user?.id}
                              defaultValue={user?.id}
                              onSelect={() => setSelectedUserId(user?.id)}
                              className={cn(
                                "my-1 capitalize",
                                selectedUserId === user.id && "bg-secondary/30 text-black",
                              )}
                            >
                              {user?.name || user?.email}
                            </CommandItem>
                          </>
                        ))}
                      </PopoverClose>
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {selectedUser && (
              <div className='mt-2 rounded-md bg-muted p-2'>
                <div className='flex items-center gap-2'>
                  <Avatar className='h-8 w-8'>
                    <AvatarImage
                      src={
                        selectedUser?.user_picture
                          ? selectedUser?.user_picture.url
                          : `${process.env.PUBLIC_URL}/usr.svg`
                      }
                      alt={selectedUser.name}
                    />
                    <AvatarFallback>{selectedUser.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className='text-sm font-medium'>{selectedUser?.name}</p>
                    <p className='text-xs text-muted-foreground'>{selectedUser?.role?.name}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className='space-y-2'>
            <Label htmlFor='user-select' className='text-sm font-medium'>
              {translate("Project")}
            </Label>
            <Popover>
              <PopoverTrigger
                disabled={assignRoleMutation.isPending}
                className='border rounded-md p-1.5 flex justify-between items-center w-full'
              >
                <div className='text-sm'>
                  {selectedProject ? (
                    <div className='w-[23ch] text-left font-semibold capitalize text-xs p-0.5 truncate text-ellipsis overflow-hidden'>
                      {selectedProject?.title}
                    </div>
                  ) : (
                    translate("Select Project")
                  )}
                </div>
                <ChevronsUpDown size={12} />
              </PopoverTrigger>

              <PopoverContent className='p-0'>
                <Command>
                  <CommandInput placeholder={translate("Search for project") + "..."} />
                  <CommandList>
                    <CommandEmpty>{translate("No projects found")}.</CommandEmpty>
                    <CommandGroup>
                      <PopoverClose className='w-full flex-1'>
                        {projects?.items?.map((project: DictType) => (
                          <CommandItem
                            key={project.id}
                            value={project?.id}
                            defaultValue={project?.id}
                            onSelect={() => setSelectedProjectId(project?.id)}
                            className={cn(
                              "my-1 capitalize",
                              selectedProjectId === project.id && "bg-secondary/30 text-black",
                            )}
                          >
                            {project?.title}
                          </CommandItem>
                        ))}
                      </PopoverClose>
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {selectedProject && (
              <div className='mt-2 rounded-md bg-muted p-2'>
                <div className='flex items-center gap-2'>
                  <div>
                    <p className='text-sm font-medium'>{selectedProject?.title}</p>
                    <p className='text-xs text-muted-foreground'>
                      {selectedProject?.description || "---"}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedUser && selectedProject && (
          <div className='mb-4 rounded-md border p-3'>
            <p className='text-sm text-center'>
              <span className='font-medium'>{selectedUser?.name}</span>{" "}
              {translate("will be assigned to")}{" "}
              <span className='font-medium'>{selectedProject?.title}</span> {translate("as")}{" "}
              <span className='font-medium'>{role?.name}</span>
            </p>
          </div>
        )}

        <div className='flex justify-end gap-2'>
          <Button variant='outline' onClick={() => setOpen(false)}>
            {translate("Cancel")}
          </Button>
          <Button
            onClick={() => assignRoleMutation.mutate()}
            disabled={!selectedUserId || !selectedProjectId}
          >
            {translate("Assign")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignProjectRoleWithoutProjectId;
